// src/pages/Users.tsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, CircularProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Avatar, Chip, Stack, Tooltip,
    FormControl, InputLabel, Select, MenuItem, IconButton, Button, TextField,
    InputAdornment, Snackbar, Alert, Dialog, DialogContent, DialogActions,
    Grid, Divider,
} from "@mui/material";
import {
    FilterList as FilterIcon, Clear as ClearIcon, Download as DownloadIcon,
    Search as SearchIcon, Delete as DeleteIcon, PersonAdd as PersonAddIcon,
    People as PeopleIcon, Refresh as RefreshIcon, Devices as DevicesIcon,
    CheckCircle as SubscribedIcon, Cancel as FreeIcon, Person as PersonIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { getUsers, deleteUser, createUser, checkUserExists, User, getFullUrl, CreateUserPayload } from "../services/userService";
import { getClasses, getMediums, getSubjects, Subject } from "../services/subjectService";
import { grantSubscription } from "../services/subscriptionService";
import { TableShimmer } from "../components/Shimmer";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(14px); }
  to   { opacity:1; transform:translateY(0); }
`;

const USER_COLORS = [
    "linear-gradient(135deg,#667eea,#764ba2)",
    "linear-gradient(135deg,#4facfe,#00f2fe)",
    "linear-gradient(135deg,#43e97b,#38f9d7)",
    "linear-gradient(135deg,#f093fb,#f5576c)",
    "linear-gradient(135deg,#fa709a,#fee140)",
    "linear-gradient(135deg,#a18cd1,#fbc2eb)",
];

function getInitials(name?: string): string {
    if (!name) return "?";
    return name.split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2);
}

const KARNATAKA_DISTRICTS = [
    "Bagalkot", "Ballari", "Belagavi", "Bengaluru Rural", "Bengaluru Urban",
    "Bidar", "Chamarajanagar", "Chikkaballapur", "Chikkamagaluru", "Chitradurga",
    "Dakshina Kannada", "Davanagere", "Dharwad", "Gadag", "Hassan",
    "Haveri", "Kalaburagi", "Kodagu", "Kolar", "Koppal",
    "Mandya", "Mysuru", "Raichur", "Ramanagara", "Shivamogga",
    "Tumakuru", "Udupi", "Uttara Kannada", "Vijayapura", "Yadgir",
];

const emptyForm: CreateUserPayload = {
    firstName: "", lastName: "", email: "", mobile: "",
    school: "", medium: "", className: "", district: "", address: "", role: "",
};

export default function UsersPage(): React.ReactElement {
    const navigate = useNavigate();

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    // Filters
    const [filterRole, setFilterRole] = useState("ALL");
    const [filterSubscribed, setFilterSubscribed] = useState("ALL");
    const [filterSessions, setFilterSessions] = useState("ALL");
    const [searchQuery, setSearchQuery] = useState("");

    // Meta
    const [availableClasses, setAvailableClasses] = useState<{ id: number; name: string }[]>([]);
    const [availableMediums, setAvailableMediums] = useState<{ id: number; name: string }[]>([]);

    useEffect(() => {
        getClasses(0, 100).then(setAvailableClasses).catch(console.error);
        getMediums().then(setAvailableMediums).catch(console.error);
    }, []);

    // Add User dialog
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState<CreateUserPayload>(emptyForm);
    const [addErrors, setAddErrors] = useState<Partial<Record<keyof CreateUserPayload, string>>>({});
    const [addLoading, setAddLoading] = useState(false);

    // Subject grant (ROLE_USER only)
    const [availableSubjects, setAvailableSubjects] = useState<Subject[]>([]);
    const [grantSubjectIds, setGrantSubjectIds] = useState<number[]>([]);
    const [grantExpiry, setGrantExpiry] = useState("");
    const [subjectsLoading, setSubjectsLoading] = useState(false);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (addForm.role !== "ROLE_USER") { setAvailableSubjects([]); setGrantSubjectIds([]); return; }
        if (!availableClasses.length) return;
        const cls = availableClasses.find(c => c.name === addForm.className);
        if (!cls) { setAvailableSubjects([]); setGrantSubjectIds([]); return; }
        setSubjectsLoading(true);
        getSubjects({ classId: cls.id })
            .then(subs => setAvailableSubjects(Array.isArray(subs) ? subs : []))
            .catch(console.error)
            .finally(() => setSubjectsLoading(false));
    }, [addForm.className, addForm.role, availableClasses]);

    // ── Fetch users ───────────────────────────────────────
    const fetchUsers = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params: any = { page: 0, size: 200 };
            if (filterRole !== "ALL") params.role = filterRole;
            if (filterSubscribed !== "ALL") params.subscribed = filterSubscribed === "TRUE";
            const data = await getUsers(params);
            setUsers(Array.isArray(data) ? data : (data?.content ?? []));
        } catch (err: any) {
            setError("Failed to load users.");
        } finally { setLoading(false); }
    }, [filterRole, filterSubscribed]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // ── Client-side search + session filter ──────────────
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                if (!u.displayName?.toLowerCase().includes(q) &&
                    !u.email?.toLowerCase().includes(q) &&
                    !u.mobile?.includes(q) &&
                    !u.district?.toLowerCase().includes(q)) return false;
            }
            if (filterSessions !== "ALL") {
                const cnt = u.sessions?.length || 0;
                if (filterSessions === "5+") return cnt >= 5;
                return cnt === Number(filterSessions);
            }
            return true;
        });
    }, [users, searchQuery, filterSessions]);

    const clearFilters = () => {
        setFilterRole("ALL"); setFilterSubscribed("ALL");
        setFilterSessions("ALL"); setSearchQuery("");
    };

    // ── Stats ─────────────────────────────────────────────
    const totalUsers = users.length;
    const subscribedCnt = users.filter(u => u.isSubscribed).length;
    const adminCnt = users.filter(u => u.role === "ROLE_ADMIN").length;

    // ── Export CSV ────────────────────────────────────────
    const handleExport = () => {
        if (!filteredUsers.length) return;
        const headers = ["ID", "Name", "Email", "Mobile", "Role", "School", "Class", "Medium", "Subscribed", "District", "Session Count"];
        const rows = filteredUsers.map(u => [
            u.id, `"${u.displayName || ""}"`, u.email, u.mobile || "", u.role,
            `"${u.school || ""}"`, u.className || "", u.medium || "",
            u.isSubscribed ? "Yes" : "No", u.district || "", u.sessions?.length || 0,
        ]);
        const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    // ── Delete ────────────────────────────────────────────
    const handleDelete = (e: React.MouseEvent, userId: number) => {
        e.stopPropagation();
        const u = users.find(u => u.id === userId);
        setDeleteTarget({ id: userId, name: u?.displayName || "this user" });
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await deleteUser(deleteTarget.id);
            setSnackbar({ type: "success", msg: "User deleted successfully" });
            fetchUsers();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Delete failed" });
        } finally {
            setDeleteLoading(false);
            setDeleteTarget(null);
        }
    };

    // ── Validate ──────────────────────────────────────────
    const validateForm = () => {
        const errs: Partial<Record<keyof CreateUserPayload, string>> = {};
        if (!addForm.firstName.trim()) errs.firstName = "First name is required";
        if (!addForm.mobile.trim()) errs.mobile = "Mobile number is required";
        else if (!/^\d{10}$/.test(addForm.mobile.trim())) errs.mobile = "Enter a valid 10-digit number";
        if (addForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email.trim())) errs.email = "Enter a valid email";
        if (!addForm.role) errs.role = "Role is required";
        setAddErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Add User ──────────────────────────────────────────
    const handleAddUser = async () => {
        if (!validateForm()) return;
        setAddLoading(true);
        try {
            try {
                const checks = await checkUserExists({ mobile: addForm.mobile.trim(), email: addForm.email?.trim() || undefined });
                const errs: any = {};
                if (checks.mobileExists) errs.mobile = "Mobile already registered";
                if (checks.emailExists) errs.email = "Email already registered";
                if (Object.keys(errs).length > 0) { setAddErrors(p => ({ ...p, ...errs })); return; }
            } catch (ce: any) {
                const s = ce?.response?.status;
                if (s !== 404 && s !== 405) throw ce;
            }

            const newUser = await createUser({
                ...addForm,
                firstName: addForm.firstName.trim(),
                mobile: addForm.mobile.trim(),
                email: addForm.email?.trim() || undefined,
            });

            if (addForm.role === "ROLE_USER" && grantSubjectIds.length > 0 && grantExpiry && newUser?.id) {
                try {
                    await grantSubscription({ userId: newUser.id, subjectIds: grantSubjectIds, expiryDate: new Date(grantExpiry).toISOString() });
                } catch {
                    setSnackbar({ type: "success", msg: "User added but subject grant failed. Grant manually." });
                    handleAddClose(); fetchUsers(); return;
                }
            }

            setSnackbar({ type: "success", msg: "User added successfully" });
            handleAddClose(); fetchUsers();
        } catch (err: any) {
            const status = err?.response?.status;
            const msg: string = err?.response?.data?.error || err.message || "";
            if (status === 409) {
                if (msg.toLowerCase().includes("mobile")) setAddErrors(p => ({ ...p, mobile: "Mobile already registered" }));
                else if (msg.toLowerCase().includes("email")) setAddErrors(p => ({ ...p, email: "Email already registered" }));
                else setSnackbar({ type: "error", msg: msg || "Duplicate user detected." });
            } else {
                setSnackbar({ type: "error", msg: msg || "Failed to add user" });
            }
        } finally { setAddLoading(false); }
    };

    const handleAddClose = () => {
        setAddOpen(false); setAddForm(emptyForm); setAddErrors({});
        setGrantSubjectIds([]); setGrantExpiry("");
    };

    // ── Session chip ──────────────────────────────────────
    const sessionChip = (sessions?: any[]) => {
        const cnt = sessions?.length || 0;
        const bg = cnt <= 1 ? "#dcfce7" : cnt === 2 ? "#fef3c7" : "#fee2e2";
        const col = cnt <= 1 ? "#166534" : cnt === 2 ? "#92400e" : "#991b1b";
        return (
            <Tooltip title={`${cnt} active session(s)`}>
                <Chip
                    icon={<DevicesIcon sx={{ fontSize: "14px !important", color: `${col} !important` }} />}
                    label={cnt}
                    size="small"
                    sx={{ background: bg, color: col, fontWeight: 700, fontSize: "0.72rem", "& .MuiChip-icon": { ml: 0.5 } }}
                />
            </Tooltip>
        );
    };

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1400, mx: "auto" }}>

                {/* ── Hero Header ── */}
                <Paper elevation={0} sx={{
                    mb: 3, borderRadius: 4, overflow: "hidden",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                    color: "#fff", animation: `${fadeUp} 0.4s ease-out`, position: "relative",
                }}>
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, right: 140, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <PeopleIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>User Management</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                    Manage registered students and admin accounts
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
                            <Chip label={`${totalUsers} Total`} sx={{ background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700 }} />
                            <Chip label={`${subscribedCnt} Premium`} sx={{ background: "rgba(67,233,123,0.25)", color: "#fff", fontWeight: 700, border: "1px solid rgba(67,233,123,0.4)" }} />
                            <Chip label={`${adminCnt} Admins`} sx={{ background: "rgba(251,191,36,0.25)", color: "#fff", fontWeight: 700, border: "1px solid rgba(251,191,36,0.4)" }} />

                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleExport}
                                size="small"
                                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", borderRadius: 2, fontWeight: 700, "&:hover": { background: "rgba(255,255,255,0.12)", borderColor: "#fff" } }}
                            >
                                Export
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddIcon />}
                                onClick={() => setAddOpen(true)}
                                size="small"
                                sx={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, borderRadius: 2, border: "1px solid rgba(255,255,255,0.35)", backdropFilter: "blur(4px)", "&:hover": { background: "rgba(255,255,255,0.3)" } }}
                            >
                                Add User
                            </Button>
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Filter + Search Toolbar ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 2, mb: 2.5, animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", minWidth: 70 }}>
                            <FilterIcon fontSize="small" />
                            <Typography variant="body2" fontWeight={700}>Filters</Typography>
                        </Box>

                        {/* Search */}
                        <TextField
                            size="small" placeholder="Search name, email, mobile, district…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
                                endAdornment: searchQuery ? <IconButton size="small" onClick={() => setSearchQuery("")}><ClearIcon fontSize="small" /></IconButton> : null,
                            }}
                            sx={{ flex: 1, minWidth: 240 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Role</InputLabel>
                            <Select value={filterRole} label="Role" onChange={e => setFilterRole(e.target.value)}>
                                <MenuItem value="ALL">All Roles</MenuItem>
                                <MenuItem value="ROLE_USER">User</MenuItem>
                                <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Subscription</InputLabel>
                            <Select value={filterSubscribed} label="Subscription" onChange={e => setFilterSubscribed(e.target.value)}>
                                <MenuItem value="ALL">All</MenuItem>
                                <MenuItem value="TRUE">Premium</MenuItem>
                                <MenuItem value="FALSE">Free</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Sessions</InputLabel>
                            <Select value={filterSessions} label="Sessions" onChange={e => setFilterSessions(e.target.value)}>
                                <MenuItem value="ALL">Any</MenuItem>
                                <MenuItem value="1">1 session</MenuItem>
                                <MenuItem value="2">2 sessions</MenuItem>
                                <MenuItem value="5+">5+ sessions</MenuItem>
                            </Select>
                        </FormControl>

                        <Tooltip title="Clear all filters">
                            <IconButton size="small" onClick={clearFilters}
                                sx={{ color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", "&:hover": { background: "rgba(239,68,68,0.08)" } }}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{ flex: 1 }} />

                        <Tooltip title="Refresh"><IconButton onClick={fetchUsers} sx={{ border: "1px solid", borderColor: "divider" }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>

                    {(searchQuery || filterRole !== "ALL" || filterSubscribed !== "ALL" || filterSessions !== "ALL") && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Showing {filteredUsers.length} of {users.length} users
                        </Typography>
                    )}
                </Paper>

                {/* ── Table ── */}
                {loading ? (
                    <TableShimmer rows={8} cols={6} />
                ) : error ? (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                        <Typography color="error">⚠ {error}</Typography>
                        <Button onClick={fetchUsers} sx={{ mt: 2 }}>Retry</Button>
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden", animation: `${fadeUp} 0.4s ease-out 120ms both` }}>
                        <TableContainer sx={{ maxHeight: "65vh" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {["User", "Contact", "Education", "Subscription", "Sessions", ""].map((h, i) => (
                                            <TableCell key={i} align={i === 5 ? "right" : "left"}
                                                sx={{ fontWeight: 700, fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", background: "#f8fafc", py: 1.5 }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user, idx) => {
                                        const gradient = USER_COLORS[user.id % USER_COLORS.length];
                                        const isAdmin = user.role === "ROLE_ADMIN";
                                        return (
                                            <TableRow
                                                key={user.id} hover
                                                sx={{ cursor: "pointer", "&:hover": { background: "#f0fafa" }, transition: "background 0.15s" }}
                                                onClick={() => navigate(`/users/${user.id}`)}
                                            >
                                                {/* User */}
                                                <TableCell>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar
                                                            src={getFullUrl(user.profilePhotoUrl)}
                                                            sx={{ width: 40, height: 40, background: gradient, fontSize: "0.8rem", fontWeight: 800, border: "2px solid #fff", boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}
                                                        >
                                                            {!user.profilePhotoUrl && getInitials(user.displayName)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={700} color={PRIMARY} sx={{ lineHeight: 1.2 }}>
                                                                {user.displayName || "Unknown"}
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>#{user.id}</Typography>
                                                                {isAdmin && <Chip label="Admin" size="small" sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, background: "#fef3c7", color: "#92400e", "& .MuiChip-label": { px: 0.75 } }} />}
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>

                                                {/* Contact */}
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: "#1e293b" }}>{user.email || "—"}</Typography>
                                                    <Typography variant="caption" color="text.disabled">{user.mobile || "—"}</Typography>
                                                </TableCell>

                                                {/* Education */}
                                                <TableCell sx={{ px: 1.5 }}>
                                                    <Typography variant="body2" sx={{ color: "#1e293b", mb: 0.4 }} noWrap>
                                                        {user.school ? (user.school.length > 24 ? user.school.slice(0, 24) + "…" : user.school) : "—"}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.5}>
                                                        {user.className && <Chip label={user.className} size="small" sx={{ height: 18, fontSize: "0.67rem", background: "rgba(23,65,67,0.08)", color: PRIMARY, fontWeight: 600, "& .MuiChip-label": { px: 0.75 } }} />}
                                                        {user.medium && <Chip label={user.medium} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.67rem", "& .MuiChip-label": { px: 0.75 } }} />}
                                                    </Stack>
                                                </TableCell>

                                                {/* Subscription */}
                                                <TableCell sx={{ px: 1.5 }}>
                                                    {user.isSubscribed
                                                        ? <Chip icon={<SubscribedIcon sx={{ fontSize: "14px !important", color: "#166534 !important" }} />} label="Premium" size="small"
                                                            sx={{ background: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: "0.72rem" }} />
                                                        : <Chip icon={<FreeIcon sx={{ fontSize: "14px !important", color: "#64748b !important" }} />} label="Free" size="small"
                                                            sx={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: "0.72rem" }} />
                                                    }
                                                </TableCell>

                                                {/* Sessions */}
                                                <TableCell>{sessionChip(user.sessions)}</TableCell>

                                                {/* Actions */}
                                                <TableCell
                                                    align="right"
                                                    onClick={e => e.stopPropagation()}
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center"
                                                        sx={{
                                                            opacity: 0.25,
                                                            transition: 'opacity 0.15s',
                                                            'tr:hover &': { opacity: 1 },
                                                        }}
                                                    >
                                                        <Tooltip title="View Profile">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate(`/users/${user.id}`)}
                                                                sx={{
                                                                    color: PRIMARY,
                                                                    border: '1px solid rgba(23,65,67,0.2)',
                                                                    borderRadius: 1.5,
                                                                    p: 0.6,
                                                                    '&:hover': { background: 'rgba(23,65,67,0.08)' },
                                                                }}
                                                            >
                                                                <PersonIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete User">
                                                            <IconButton
                                                                size="small"
                                                                onClick={e => handleDelete(e, user.id)}
                                                                sx={{
                                                                    color: '#ef4444',
                                                                    border: '1px solid rgba(239,68,68,0.25)',
                                                                    borderRadius: 1.5,
                                                                    p: 0.6,
                                                                    '&:hover': { background: 'rgba(239,68,68,0.08)', borderColor: '#ef4444' },
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {filteredUsers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 8, color: "text.disabled" }}>
                                                <PeopleIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: "block", mx: "auto" }} />
                                                No users match your filters.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Box>

            {/* ══ Add User Dialog ══ */}
            <Dialog open={addOpen} onClose={handleAddClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                {/* Gradient header */}
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: "#fff" }}>
                    <Typography variant="h6" fontWeight={800}>Add New User</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>Fill in the details to create a new account</Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2.5 }}>
                    <Grid container spacing={2}>
                        {/* Name row */}
                        <Grid size={{ xs: 6 }}>
                            <TextField label="First Name *" fullWidth size="small" autoFocus
                                value={addForm.firstName} onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))}
                                error={!!addErrors.firstName} helperText={addErrors.firstName} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField label="Last Name" fullWidth size="small"
                                value={addForm.lastName} onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))} />
                        </Grid>

                        {/* Contact row */}
                        <Grid size={{ xs: 6 }}>
                            <TextField label="Mobile *" fullWidth size="small"
                                value={addForm.mobile} onChange={e => setAddForm(f => ({ ...f, mobile: e.target.value }))}
                                error={!!addErrors.mobile} helperText={addErrors.mobile} inputProps={{ maxLength: 10 }} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField label="Email" fullWidth size="small"
                                value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                                error={!!addErrors.email} helperText={addErrors.email} />
                        </Grid>

                        {/* Role */}
                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth size="small" error={!!addErrors.role}>
                                <InputLabel shrink>Role *</InputLabel>
                                <Select value={addForm.role} label="Role *" notched displayEmpty
                                    onChange={e => { setAddForm(f => ({ ...f, role: e.target.value })); setGrantSubjectIds([]); setGrantExpiry(""); }}
                                    renderValue={val => val === "ROLE_USER" ? "User" : val === "ROLE_ADMIN" ? "Admin" : <em style={{ color: "#9ca3af" }}>Select a role…</em>}
                                >
                                    <MenuItem value="" disabled><em>Select a role…</em></MenuItem>
                                    <MenuItem value="ROLE_USER">User (Student)</MenuItem>
                                    <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                                </Select>
                                {addErrors.role && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{addErrors.role}</Typography>}
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}><Divider><Chip label="Education Details" size="small" sx={{ fontWeight: 700, color: PRIMARY }} /></Divider></Grid>

                        {/* School + class + medium row */}
                        <Grid size={{ xs: 12 }}>
                            <TextField label="School" fullWidth size="small"
                                value={addForm.school} onChange={e => setAddForm(f => ({ ...f, school: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Class</InputLabel>
                                <Select label="Class" value={addForm.className || ""} onChange={e => setAddForm(f => ({ ...f, className: e.target.value }))}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {availableClasses.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Medium</InputLabel>
                                <Select label="Medium" value={addForm.medium || ""} onChange={e => setAddForm(f => ({ ...f, medium: e.target.value }))}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {availableMediums.map(m => <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* District + address */}
                        <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel shrink>District</InputLabel>
                                <Select value={addForm.district || ""} label="District" notched displayEmpty
                                    onChange={e => setAddForm(f => ({ ...f, district: e.target.value }))}
                                    renderValue={v => v || <em style={{ color: "#9ca3af" }}>Select district…</em>}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {KARNATAKA_DISTRICTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField label="Address" fullWidth size="small"
                                value={addForm.address} onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} />
                        </Grid>

                        {/* Subject Grant (ROLE_USER only) */}
                        {addForm.role === "ROLE_USER" && (
                            <>
                                <Grid size={{ xs: 12 }}>
                                    <Divider><Chip label="Subject Access (optional)" size="small" sx={{ fontWeight: 700, color: PRIMARY }} /></Divider>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    {!addForm.className ? (
                                        <Typography variant="caption" color="text.secondary">Select a Class above to see available subjects.</Typography>
                                    ) : subjectsLoading ? (
                                        <CircularProgress size={18} sx={{ color: PRIMARY }} />
                                    ) : availableSubjects.length === 0 ? (
                                        <Typography variant="caption" color="text.secondary">No subjects found for selected class.</Typography>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Subjects</InputLabel>
                                                <Select multiple value={grantSubjectIds} label="Subjects"
                                                    onChange={e => setGrantSubjectIds(e.target.value as number[])}
                                                    renderValue={sel => (sel as number[]).map(id => availableSubjects.find(s => s.id === id)?.name || id).join(", ")}
                                                >
                                                    {availableSubjects.map(s => (
                                                        <MenuItem key={s.id} value={s.id}>
                                                            <input type="checkbox" checked={grantSubjectIds.includes(s.id)} readOnly style={{ marginRight: 8 }} />
                                                            {s.name}{s.medium?.name ? ` (${s.medium.name})` : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {grantSubjectIds.length > 0 && (
                                                <TextField label="Access Expiry Date *" type="date" size="small" fullWidth
                                                    value={grantExpiry} onChange={e => setGrantExpiry(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                                                />
                                            )}
                                        </Stack>
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleAddClose} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleAddUser} disabled={addLoading}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}>
                        {addLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Add User"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ══ Delete Confirmation Dialog ══ */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', px: 3, py: 2.5, color: '#fff' }}>
                    <Typography variant="h6" fontWeight={800}>Delete User</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>This action cannot be undone</Typography>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong>?
                        All their subscriptions, sessions, and data will be removed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={deleteLoading}
                        sx={{ background: '#dc2626', fontWeight: 700, px: 3, borderRadius: 2, '&:hover': { background: '#b91c1c' } }}
                    >
                        {deleteLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete Permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}